import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import AuthInput from "../AuthInput";

const ForgotPasswordStep = ({
  step,
  email,
  onBack,
  otp,
  setOtp,
  seconds,
  loading,
  error,
  otpInitialized,
  otpExpired,
  onOtpInit,
  onOtpVerify,
  onOtpResend,
  setError,
}) => {
  const [showAdminPopup, setShowAdminPopup] = useState(false);
  // Flag to ensure OTP API is called only once
  const otpInitCalled = useRef(false);
  useEffect(() => {
    if (!otpInitCalled.current) {
      onOtpInit?.();
      otpInitCalled.current = true;
    }
  }, [onOtpInit]);

  const formatTime = (totalSeconds) => {
    const safe = Number.isFinite(totalSeconds) ? Math.max(totalSeconds, 0) : 0;
    const min = Math.floor(safe / 60);
    const sec = safe % 60;
    return `${min}:${sec.toString().padStart(2, "0")}`;
  };

  // Resend enable condition
  const canResend = Number.isFinite(seconds) && seconds <= 0 && !loading;

  const handleResend = () => {
    if (!canResend) return;
    onOtpResend?.();
    // Parent must reset seconds (e.g., setSeconds(60))
  };

  return (
    <div>
      <div className="text-center pt-1 pb-3">
        <p className="text-sm text-primary sm:whitespace-nowrap">
          We&apos;ve sent a one-time code to{" "}
          <span className="font-semibold text-secondary">{email}</span> to reset
          your password.
        </p>
        {/* <p className="text-sm text-primary pt-1 sm:whitespace-nowrap">
          Enter the OTP sent to your email to continue.
        </p> */}
      </div>

      <div className="pt-2">
        <AuthInput
          name="otp"
          value={otp}
          setValue={setOtp}
          step={step}
          onChange={(e) =>
            setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          placeholder="Enter the OTP"
          buttonText={loading ? "Please wait..." : "Verify"}
          onButtonClick={onOtpVerify}
          loading={loading}
          error={error}
          disabled={otpExpired}
          setError={setError}
        />

        <div className="w-full sm:w-[50%] mx-auto flex items-center justify-between pt-3">
          <p className="text-sm text-gray-500">
            OTP expires in{" "}
            <span className="text-red-500 font-semibold">
              {Number.isFinite(seconds) ? formatTime(seconds) : "0:00"}
            </span>
          </p>

          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend}
            className={`text-sm ${
              canResend
                ? "text-buttonColor underline"
                : "text-gray-400 cursor-not-allowed"
            }`}
          >
            Resend OTP
          </button>
        </div>

        <div className="w-full sm:w-[100%] mx-auto text-primary text-center space-y-1 pt-4">
          <p>
            Please check your Inbox for the verification code. If you don&apos;t
            see it, check your Spam or Junk folder.
          </p>
          <p>
            If you don&apos;t receive the code before it expires, you can{" "}
            <button
              type="button"
              onClick={() => setShowAdminPopup(true)}
              className="text-buttonColor underline"
            >
              contact administrator
            </button>
            .
          </p>
        </div>
      </div>

      {showAdminPopup ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full text-center">
            <Info
              size={34}
              className="mx-auto text-blue-600"
              aria-hidden="true"
            />
            <h3 className="text-base font-semibold pt-2">
              Contact Administrator
            </h3>
            <p className="text-sm text-primary pt-2">
              Need help with login? Contact us:
            </p>
            <p className="text-sm text-primary pt-2">
              Call:{" "}
              <a
                href="tel:+18604022247"
                className="text-secondary underline font-semibold"
              >
                +1 860 402 2247
              </a>
            </p>
            <p className="text-sm text-primary pt-1">
              Email:{" "}
              <a
                href="mailto:info@biblicaltouring.com"
                className="text-secondary underline font-semibold"
              >
                info@biblicaltouring.com
              </a>
            </p>
            <button
              type="button"
              onClick={() => setShowAdminPopup(false)}
              className="mt-4 bg-buttonColor text-white text-xs px-4 py-2 rounded-md"
            >
              Ok
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ForgotPasswordStep;
